import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Swords, ShieldAlert, CheckCircle2, RefreshCw, Ban } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { PageHeader, Loading, StatCard, Card, SeverityBadge, StatusBadge, SandboxTag, CHART_COLORS } from "../components/common";
import { toast } from "sonner";

export default function RedTeam() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState(null);
  const [sev, setSev] = useState("");

  const load = () => {
    api.get("/redteam/dashboard/stats").then((r) => setStats(r.data));
    api.get("/redteam", { params: sev ? { severity: sev } : {} }).then((r) => setRows(r.data));
  };
  useEffect(load, [sev]);

  const retest = async (id) => {
    const { data } = await api.post(`/redteam/${id}/retest`);
    toast.success(`Retest ${data.result} — SIMULATED`);
    load();
  };

  if (!stats || !rows) return <Loading />;

  const summary = `AI Red Team centre. ${stats.total} simulated tests. ${stats.open} open findings, ${stats.critical} critical and ${stats.high} high severity. All red team activity is simulated against sandbox targets only.`;

  return (
    <div>
      <PageHeader title={t("nav.redteam")} subtitle="All tests are SIMULATED against sandbox targets — no real systems attacked." summary={summary}>
        <SandboxTag text="SIMULATED – SANDBOX MODE" />
      </PageHeader>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-6">
        <StatCard testid="rt-total" label="Total Tests" value={stats.total} icon={Swords} />
        <StatCard testid="rt-open" label="Open Findings" value={stats.open} icon={ShieldAlert} accent="bg-orange-50 text-orange-600" />
        <StatCard testid="rt-critical" label="Critical" value={stats.critical} icon={ShieldAlert} accent="bg-red-50 text-red-600" onClick={() => setSev("Critical")} />
        <StatCard testid="rt-high" label="High" value={stats.high} icon={ShieldAlert} accent="bg-orange-50 text-orange-600" onClick={() => setSev("High")} />
        <StatCard testid="rt-completed" label="Completed" value={stats.completed} icon={CheckCircle2} accent="bg-emerald-50 text-emerald-600" />
        <StatCard testid="rt-coverage" label="Coverage %" value={`${stats.coverage}%`} icon={CheckCircle2} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card title="Findings by Severity">
          <ResponsiveContainer width="100%" height={250}>
            <PieChart><Pie data={stats.by_severity} dataKey="value" nameKey="name" outerRadius={90}>{stats.by_severity.map((e, i) => <Cell key={i} fill={{ Critical: "#DC2626", High: "#EA580C", Medium: "#D97706", Low: "#2563EB" }[e.name] || CHART_COLORS[i]} />)}</Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Findings by Category">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={stats.by_category.slice(0, 12)} margin={{ left: -20 }}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} /><XAxis dataKey="name" tick={{ fontSize: 8 }} angle={-40} textAnchor="end" height={90} interval={0} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="value" radius={[5, 5, 0, 0]} fill="#8A1538" /></BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {sev && <button onClick={() => setSev("")} className="mt-4 text-sm text-[var(--qatar)] underline">Clear filter: {sev}</button>}

      <Card title="Red Team Tests" className="mt-6" right={<SandboxTag />}>
        <div className="space-y-2">
          {rows.map((rt) => (
            <div key={rt.id} data-testid={`rt-row-${rt.id}`} className="flex flex-col gap-2 rounded-xl border border-slate-100 p-3 md:flex-row md:items-center md:justify-between">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs font-bold text-[var(--qatar)]">{rt.id}</span>
                  <SeverityBadge level={rt.severity} /><StatusBadge status={rt.status} />
                  <button onClick={() => nav(`/usecases/${rt.usecase_id}`)} className="text-xs text-slate-500 underline">{rt.usecase_id}</button>
                </div>
                <p className="mt-1 text-sm font-semibold text-slate-800">{rt.category} <span className="font-normal text-slate-500">— {rt.usecase_name}</span></p>
                <p className="text-xs text-slate-500">{rt.attack_scenario}</p>
              </div>
              {rt.status === "Open" ? (
                <button data-testid={`rt-retest-${rt.id}`} onClick={() => retest(rt.id)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"><RefreshCw className="h-3.5 w-3.5" /> {t("retest")}</button>
              ) : <span className="text-xs font-medium text-emerald-600">{rt.retest_result || "Closed"}</span>}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
